import { create, StateCreator, StoreApi, UseBoundStore } from 'zustand';

export type Setters<T> = {
  [K in keyof T as `set${Capitalize<string & K>}`]: (value: T[K] | ((prev: T[K]) => T[K])) => void;
};

export type StoreSetter<T> = (value: T | ((prev: T) => T)) => void;

// 깊은 복사를 수행하는 함수 (Set 객체 지원)
const deepCopy = <T>(obj: T): T => {
  if (obj === null || typeof obj !== 'object') return obj;

  // Set 객체인 경우
  if (obj instanceof Set) {
    return new Set([...obj]) as unknown as T;
  }

  // Map 객체인 경우
  if (obj instanceof Map) {
    return new Map([...obj].map(([k, v]) => [k, deepCopy(v)])) as unknown as T;
  }

  // 배열인 경우
  if (Array.isArray(obj)) {
    return obj.map(item => deepCopy(item)) as unknown as T;
  }

  // 일반 객체인 경우
  const copy = {} as Record<string, any>;
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      copy[key] = deepCopy((obj as any)[key]);
    }
  }
  return copy as T;
};

// logger 미들웨어 함수 - storeName을 받아서 사용
const logger =
  <T extends object>(storeName: string, config: StateCreator<T>): StateCreator<T> =>
  (set, get, api) =>
    config(
      args => {
        const prevState = get(); // 이전 상태 저장
        set(args); // 상태 업데이트
        const newState = get(); // 새로운 상태 저장

        // 변경된 부분을 추적하는 객체 생성
        const changedProps = Object.keys(newState).reduce((acc, key) => {
          const typedKey = key as keyof T;
          if (prevState[typedKey] !== newState[typedKey]) {
            acc[typedKey] = { from: prevState[typedKey], to: newState[typedKey] };
          }
          return acc;
        }, {} as Partial<Record<keyof T, { from: T[keyof T]; to: T[keyof T] }>>);

        console.log(`[STORE] [${storeName.toUpperCase()}]`, {
          prevState,
          newState,
          changedProps, // 변경된 부분 출력
        });
      },
      get,
      api,
    );

export function createStoreWithSetters<T extends object>(
  initialState: T,
  storeName: string, // storeName 매개변수 추가
): UseBoundStore<
  StoreApi<T & Setters<T> & { reset: () => void; setMultiple: (values: Partial<T>) => void }>
> {
  return create<T & Setters<T> & { reset: () => void; setMultiple: (values: Partial<T>) => void }>(
    logger(storeName, set => {
      // 초기 상태 깊은 복사로 저장
      const initial = deepCopy(initialState);

      // 자동으로 setter 생성
      const setters = Object.keys(initialState).reduce((acc, key) => {
        const setterName = `set${key.charAt(0).toUpperCase()}${key.slice(1)}` as keyof Setters<T>;
        acc[setterName] = ((value: T[keyof T] | ((prev: T[keyof T]) => T[keyof T])) => {
          set(state => {
            const typedKey = key as keyof T;
            const newValue =
              typeof value === 'function'
                ? (value as (prev: T[keyof T]) => T[keyof T])(state[typedKey])
                : value;
            return {
              ...state,
              [typedKey]: newValue,
            };
          });
        }) as Setters<T>[keyof Setters<T>];
        return acc;
      }, {} as Setters<T>);

      // reset 함수 추가 - 깊은 복사를 사용하여 initial의 완전한 복사본 반환
      const reset = () => set(() => deepCopy(initial));

      // setMultiple 함수 추가: 전달된 객체의 여러 값을 한 번에 업데이트
      const setMultiple = (values: Partial<T>) => {
        set(state => ({
          ...state,
          ...values,
        }));
      };

      return {
        ...initialState,
        ...setters,
        reset, // reset 함수 추가
        setMultiple, // 여러 값을 한 번에 설정하는 함수 추가
      };
    }),
  );
}
